import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../firebase";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Spinner from "../components/Spinner";
import { format } from "date-fns";
import { MdKeyboardArrowDown } from "react-icons/md";
import PublicHero from "../components/PublicHero";

const blogsCollectionSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Mission Movement Artikelen",
  url: "https://missionmovement.nl/blogs",
  inLanguage: "nl-NL",
  description:
    "Artikelen en inzichten van Mission Movement over fysieke voorbereiding, mentale weerbaarheid, belastbaarheid en training richting Defensie en veiligheidsdiensten.",
  publisher: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
    logo: {
      "@type": "ImageObject",
      url: "https://missionmovement.nl/img/mmvmtlogo.png",
    },
  },
  about: [
    {
      "@type": "Thing",
      name: "Militaire voorbereiding",
    },
    {
      "@type": "Thing",
      name: "Defensie voorbereiding",
    },
    {
      "@type": "Thing",
      name: "Veiligheidsdiensten voorbereiding",
    },
    {
      "@type": "Thing",
      name: "Mentale weerbaarheid",
    },
  ],
};

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allTags, setAllTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("newest");

  const blogsPerPage = 4;

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const q = query(collection(db, "blogs"), orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        const blogData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const sorted = blogData.sort(
          (a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0),
        );
        setBlogs(sorted);

        const tags = [
          "all",
          ...new Set(sorted.flatMap((blog) => blog.tags || [])),
        ];
        setAllTags(tags);
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const filteredBlogs = blogs
    .filter((blog) => {
      const matchesTag =
        selectedTag === "all" || blog.tags?.includes(selectedTag);
      const matchesSearch =
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.summary.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesTag && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === "newest") {
        return b.createdAt?.seconds - a.createdAt?.seconds;
      }
      if (sortBy === "title") {
        return a.title.localeCompare(b.title);
      }
      return 0;
    });

  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);
  const indexOfLast = currentPage * blogsPerPage;
  const indexOfFirst = indexOfLast - blogsPerPage;
  const currentBlogs = filteredBlogs.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, selectedTag, searchTerm, sortBy]);

  return (
    <div className="bg-mmPage min-h-screen">
      <Helmet>
        <title>Blogs | Militaire Voorbereiding & Training voor Veiligheidsdiensten</title>
        <meta
          name="description"
          content="Artikelen over trainen voor Defensie, politie, brandweer, KMar, belastbaarheid, discipline en mentale voorbereiding."
        />
        <meta
          property="og:title"
          content="Blogs | Militaire Voorbereiding & Training voor Veiligheidsdiensten"
        />
        <meta
          property="og:description"
          content="Artikelen over trainen voor Defensie, politie, brandweer, KMar, belastbaarheid, discipline en mentale voorbereiding."
        />
        <meta
          property="og:image"
          content="https://missionmovement.nl/img/thestory.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:url"
          content="https://missionmovement.nl/blogs"
        />
        <link
          rel="canonical"
          href="https://missionmovement.nl/blogs"
        />
        <script type="application/ld+json">
          {JSON.stringify(blogsCollectionSchema)}
        </script>
      </Helmet>

      <PublicHero
        eyebrow="Artikelen"
        title="Mission Artikelen"
        imageSrc="/img/blog-thumbnails/training-translates.jpg"
        imageAlt="Mission Movement artikelen"
        imageClassName="object-center"
      />

      <section className="px-6 md:px-10 lg:px-20 py-12">
        {/* Search & Sort */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
          <input
            type="text"
            placeholder="Zoek artikelen..."
            className="w-full md:max-w-sm px-4 py-2 text-sm rounded border border-mmBorder bg-mmSurface text-mmText placeholder:text-mmTextMuted"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="appearance-none px-4 py-2 pr-6 bg-mmSurface border border-mmBorder text-mmText rounded text-sm w-full"
            >
              <option value="newest">Sorteer: Nieuwste</option>
              <option value="title">Sorteer: Titel (A-Z)</option>
            </select>

            {/* Custom arrow */}
            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-mmAccent text-sm">
              <MdKeyboardArrowDown />
            </div>
          </div>
        </div>

        {/* Tag Filter */}
        <div className="overflow-x-auto whitespace-nowrap hide-scrollbar mb-10 pb-2 border-b border-mmBorder">
          <div className="inline-flex gap-2">
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => {
                  setSelectedTag(tag);
                  setCurrentPage(1);
                }}
                className={`uppercase px-4 py-1 text-xs md:text-sm border rounded-full transition-all whitespace-nowrap ${
                  selectedTag === tag
                    ? "bg-mmAccent text-white font-bold border-mmAccent"
                    : "border-mmBorder text-mmTextMuted hover:border-mmAccent hover:text-mmText"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Cards */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner color="border-mmAccent" />
          </div>
        ) : currentBlogs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {currentBlogs.map((blog) => (
              <Link
                to={`/blogs/${blog.slug}`}
                key={blog.id}
                className="group bg-mmSurface border border-mmBorder rounded-2xl shadow-sm hover:scale-[1.02] transition-transform flex flex-col"
              >
                <img
                  src={blog.thumbnail}
                  alt={blog.title}
                  width={800}
                  height={450}
                  loading="lazy"
                  decoding="async"
                  className="rounded-t-xl h-48 w-full object-cover"
                />
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex flex-col items-start gap-2 mb-2 sm:flex-row sm:items-start sm:justify-between">
                    <h2 className="text-mmText text-xl font-display uppercase tracking-widest break-words">
                      {blog.title}
                    </h2>
                    {blog.pinned && (
                      <span className="text-xs text-white bg-mmAccent px-2 py-1 rounded-full font-bold uppercase shrink-0">
                        aanbevolen
                      </span>
                    )}
                  </div>

                  <p className="text-mmTextMuted italic mb-2 text-base md:text-lg">
                    door {blog.author} •{" "}
                    {blog.createdAt?.seconds &&
                      format(
                        new Date(blog.createdAt.seconds * 1000),
                        "dd MMM yyyy",
                      )}
                  </p>

                  <p className="text-mmTextMuted leading-relaxed line-clamp-3 mb-6 text-base md:text-lg">
                    {blog.summary}
                  </p>

                  <div className="mt-auto">
                    <div className="mm-btn w-max">
                      <span>Lees meer</span>
                      <span className="shrink-0">→</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-mmTextMuted text-center mt-10">
            Geen artikelen gevonden.
          </p>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center mt-12 gap-2">
            {[...Array(totalPages).keys()].map((num) => (
              <button
                key={num}
                onClick={() => setCurrentPage(num + 1)}
                className={`w-10 h-10 rounded-full border border-mmBorder text-sm transition ${
                  currentPage === num + 1
                    ? "bg-mmAccent text-white font-bold border-mmAccent"
                    : "text-mmTextMuted hover:border-mmAccent hover:text-mmText"
                }`}
              >
                {num + 1}
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Blogs;
